<script setup lang="ts">
import {computed, ref, PropType} from 'vue'
import {useRoute} from 'vue-router'
import { useProjectStore } from '@/stores/project'
import { useUserStore } from '@/stores/user'
import { useAccessStore } from '@/stores/access'
import { toTypedSchema } from '@/lib/zodTypedSchema'
import * as z from 'zod'

import type { Member } from '@/types'
import type { Role } from '@/utils/accessDomains'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import {Switch} from '@/components/ui/switch'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'

const projectStore = useProjectStore()

const userStore = useUserStore()

const accessStore = useAccessStore()
const route = useRoute()

const currentMember = computed(() => userStore.member)
const projectId = route.params.project_id

const isOpen = ref(false)

const props = defineProps({
  member: {
    type: Object as PropType<Member>,
    default: null
  },
  editMode: {
    type: Boolean as PropType<boolean>,
    default: false
  },
})

const isEditMode = computed(() => props.editMode && props.member)
const roles = computed<Role[]>(() => accessStore.roles('project'))

const open = () => {
  isOpen.value = true
}

const close = () => {
  isOpen.value = false
}

const formSchema = toTypedSchema(z.object({
  name: z.string().min(3).max(255).default(isEditMode.value ? props.member.display_name : ''),
  email: z.string().email().prefault(isEditMode.value ? props.member.email : ''),
  role_id: z.string().default(
    String((isEditMode.value ? props.member.role_id : null) ?? accessStore.defaultJoinRoleId ?? '')
  ),
}))

const onSubmit = async (values: any) => {
  const roleId = Number(values.role_id)
  if (isEditMode.value) {
    if (roleId !== props.member.role_id) {
      await projectStore.setMemberRole({member: props.member, roleId})
    }
  } else {
    await projectStore.addInvite({
      member_id: currentMember.value.member_id,
      project_id: projectId,
      ...values,
      role_id: roleId,
    })
    toast({title: `Invite sent to ${values.email}`,})
  }


  close()
}

defineExpose({
  open
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogContent class="max-w-[350px]">
      <DialogHeader>
        <DialogTitle v-if="editMode">Edit member</DialogTitle>
        <DialogTitle v-else>Add member</DialogTitle>
        <DialogDescription></DialogDescription>
      </DialogHeader>

      <Form class="space-y-3" :validation-schema="formSchema" @submit="onSubmit">
        <FormField v-slot="{ componentField }" name="name">
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input v-bind="componentField" :disabled="editMode" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="email">
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input v-bind="componentField" :disabled="editMode" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="role_id">
          <FormItem>
            <FormControl>
              <RadioGroup v-bind="componentField">
                <FormItem v-for="role in roles" :key="role.role_id" class="flex items-center space-y-0 gap-x-3">
                  <FormControl>
                    <RadioGroupItem :value="String(role.role_id)" />
                  </FormControl>
                  <FormLabel class="font-normal">{{ role.name }}</FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
          </FormItem>
          <FormMessage />
        </FormField>

        <DialogFooter>
          <Button type="button" variant="ghost" @click="close">Close</Button>
          <Button type="submit" v-if="editMode">Save changes</Button>
          <Button type="submit" v-else>Invite</Button>
        </DialogFooter>
      </Form>
    </DialogContent>
  </Dialog>
</template>
